import React, { createContext, use, useEffect, useCallback, ReactNode } from "react";
import { getAllFeatureFlags, type GetAllFeatureFlagsResponseBody } from "services/onlineApplicationService";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { fetchFlagsStart, fetchFlagsSuccess, fetchFlagsFailure } from "store/feature-flags/featureFlagsSlice";

/**
 * Feature flag context value interface
 */
export interface FeatureFlagContextValue {
    featureFlags: GetAllFeatureFlagsResponseBody | null;
    isLoading: boolean;
    error: Error | null;
    /**
     * Get a feature flag by its flagKey (e.g., "modern_route")
     * @param flagKey - The key of the feature flag
     * @returns The feature flag object or undefined if not found
     */
    getFlagByKey: (flagKey: string) => GetAllFeatureFlagsResponseBody[number] | undefined;
    /**
     * Check if a feature flag is enabled by its flagKey
     * @param flagKey - The key of the feature flag
     * @returns true if the flag exists and is enabled, false otherwise
     */
    isFlagEnabled: (flagKey: string) => boolean;
}

/**
 * FeatureFlagContext
 * Provides feature flags throughout the application
 */
const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

interface FeatureFlagProviderProps {
    children: ReactNode;
}

/**
 * FeatureFlagProvider
 * Fetches all feature flags on mount and stores them in Redux
 * Persisted flags are restored automatically via redux-persist
 */
export const FeatureFlagProvider = ({ children }: FeatureFlagProviderProps) => {
    const dispatch = useAppDispatch();
    const { flags, isLoading, error } = useAppSelector((state) => state.featureFlags);

    // Memoize helper functions to avoid recreating on each render
    const getFlagByKey = useCallback(
        (flagKey: string) => {
            return flags?.find((flag) => flag.flagKey === flagKey);
        },
        [flags]
    );

    const isFlagEnabled = useCallback(
        (flagKey: string) => {
            const flag = getFlagByKey(flagKey);
            return flag?.isEnabled ?? false;
        },
        [getFlagByKey]
    );

    useEffect(() => {
        // Only fetch if we don't have cached flags (from redux-persist or previous fetch)
        if (flags) {
            return;
        }

        const fetchFeatureFlags = async () => {
            dispatch(fetchFlagsStart());

            try {
                const fetchedFlags = await getAllFeatureFlags();
                dispatch(fetchFlagsSuccess(fetchedFlags));
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : String(err);
                console.error("Failed to fetch feature flags:", err);
                dispatch(fetchFlagsFailure(errorMessage));
            }
        };

        fetchFeatureFlags();
    }, [dispatch, flags]);

    const value: FeatureFlagContextValue = {
        featureFlags: flags,
        isLoading,
        error: error ? new Error(error) : null,
        getFlagByKey,
        isFlagEnabled,
    };

    return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};

/**
 * useFeatureFlagContext hook
 * Access feature flags from any component
 * @returns {FeatureFlagContextValue} { featureFlags, isLoading, error, getFlagByKey, isFlagEnabled }
 */
export const useFeatureFlagContext = (): FeatureFlagContextValue => {
    const context = use(FeatureFlagContext);

    if (!context) {
        // Return safe defaults when provider is not available (e.g., in tests)
        return {
            featureFlags: [],
            isLoading: false,
            error: null,
            getFlagByKey: () => undefined,
            isFlagEnabled: () => false,
        };
    }

    return context;
};
